import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Kalendaryo from 'kalendaryo';
import Downshift from 'downshift';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@loyalup/core';
import {
  isSameDay,
  isSameMonth,
  min,
  max,
  isWithinInterval,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  addWeeks,
  addMonths,
  addQuarters,
  addYears,
  addDays,
  differenceInDays,
} from 'date-fns';

import Button from '../Button';

import { ReactComponent as PrevIcon } from 'icons/prev.svg';
import { ReactComponent as RightArrow } from 'icons/right-arrow.svg';

import s from './DatePeriodPicker.module.css';

const DateShortcuts = ({ t, mode, setMode, setSelection }) => (
  <>
    <Button
      className={cn(s.shortcut, mode === 'week' && s.shortcutSelected)}
      theme="text"
      onClick={() => {
        setMode('week');
        setSelection({
          start: startOfDay(startOfWeek(new Date(), { weekStartsOn: 1 })),
          end: endOfDay(endOfWeek(new Date(), { weekStartsOn: 1 })),
        });
      }}
    >
      {t('Неделя')}
    </Button>
    <Button
      className={cn(s.shortcut, mode === 'month' && s.shortcutSelected)}
      theme="text"
      onClick={() => {
        setMode('month');
        setSelection({
          start: startOfDay(startOfMonth(new Date())),
          end: endOfDay(endOfMonth(new Date())),
        });
      }}
    >
      {t('Месяц')}
    </Button>
    <Button
      className={cn(s.shortcut, mode === 'quarter' && s.shortcutSelected)}
      theme="text"
      onClick={() => {
        setMode('quarter');
        setSelection({
          start: startOfDay(startOfQuarter(new Date())),
          end: endOfDay(endOfQuarter(new Date())),
        });
      }}
    >
      {t('Квартал')}
    </Button>
    <Button
      className={cn(s.shortcut, mode === 'year' && s.shortcutSelected)}
      theme="text"
      onClick={() => {
        setMode('year');
        setSelection({
          start: startOfDay(startOfYear(new Date())),
          end: endOfDay(endOfYear(new Date())),
        });
      }}
    >
      {t('Год')}
    </Button>
  </>
);

const Month = ({
  monthDate,
  getDayLabelsInWeek,
  getWeeksInMonth,
  getItemProps,
  getFormattedDate,
  selection,
  setSelection,
  setLocalMode,
}) => {
  const today = new Date();
  const weeks = getWeeksInMonth(monthDate);
  const dayLabels = getDayLabelsInWeek('eeeeee');
  const gItemProprs = getItemProps || (props => props);

  return (
    <div className={s.month}>
      <div className={s.mheader}>{getFormattedDate(monthDate, 'LLLL y')}</div>
      <div className={cn(s.wdays, s.wheader)}>
        {dayLabels.map(label => (
          <div className={s.dayContainer} key={label}>
            <div className={s.day} key={label}>
              {label}
            </div>
          </div>
        ))}
      </div>

      {weeks.map((week, i) => (
        <div className={s.wdays} key={i}>
          {week.map(day => (
            <div
              key={day.label}
              className={cn(s.dayContainer, {
                [s.selected]: !selection.initial && selection.start && selection.end,
                [s.hidden]: !isSameMonth(monthDate, day.dateValue),
                [s.preSelected]:
                  selection.start && isWithinInterval(day.dateValue, { start: selection.start, end: selection.end }),
                [s.selectionStart]: selection.start && isSameDay(selection.start, day.dateValue),
                [s.selectionEnd]: selection.end && isSameDay(selection.end, day.dateValue),
              })}
            >
              {isSameMonth(monthDate, day.dateValue) && (
                <div
                  {...gItemProprs({
                    key: day.label,
                    item: day.dateValue,
                    onMouseEnter: e => {
                      if (selection.initial) {
                        const start = min([selection.initial, day.dateValue]);
                        const end = max([selection.initial, day.dateValue]);

                        setSelection({ ...selection, start, end });
                      }
                    },
                    onClick: e => {
                      if (selection.initial) {
                        setSelection({ ...selection, initial: null });
                        setLocalMode('custom');
                      } else {
                        setSelection({
                          start: day.dateValue,
                          end: day.dateValue,
                          initial: day.dateValue,
                        });
                      }
                      // fix issue with putting select inside <label> (see Field)
                      e.preventDefault();
                    },
                    className: cn(s.day, {
                      [s.today]: isSameDay(today, day.dateValue),
                    }),
                  })}
                >
                  {day.label}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const CalendarPeriodWidget = ({
  getDateNextMonth,
  getDatePrevMonth,
  setDatePrevMonth,
  setDateNextMonth,
  t,
  date,
  className,
  theme,
  setValue,
  setMode,
  ...opts
}) => {
  const prevMonth = getDatePrevMonth();
  const nextMonth = getDateNextMonth();
  const { selection, setSelection, localMode, setLocalMode } = opts;

  return (
    <div className={cn(s.root, className, s[theme])}>
      <div className={s.calendar}>
        <button type="button" className={s.prev} onClick={setDatePrevMonth}>
          <PrevIcon />
        </button>
        <div className={s.mcontainer}>
          <Month monthDate={prevMonth} {...opts} />
          <Month monthDate={date} {...opts} />
          <Month monthDate={nextMonth} {...opts} />
        </div>
        <button type="button" className={s.next} onClick={setDateNextMonth}>
          <PrevIcon />
        </button>
      </div>
      <div className={s.controls}>
        <DateShortcuts t={t} setSelection={setSelection} mode={localMode} setMode={setLocalMode} />
        <div className={s.period}>
          {t('{{date, date}}', { date: selection.start })} — {t('{{date, date}}', { date: selection.end })}
        </div>
        <Button
          fixedWidth={150}
          center
          onClick={() => {
            setMode(localMode);
            setValue({ start: startOfDay(selection.start), end: endOfDay(selection.end) });
          }}
        >
          {t('Применить')}
        </Button>
      </div>
    </div>
  );
};

const DatePopup = ({ value, mode, ...opts }) => {
  const [t, i18n] = useTranslation();
  const [date, setDate] = useState(value.start);
  const locale = getDateLocale(i18n.languages[0]);
  const [selection, setSelection] = useState({ start: value.start, end: value.end, initial: null });
  const [localMode, setLocalMode] = useState(mode);
  return (
    <Kalendaryo
      render={CalendarPeriodWidget}
      startWeekAt={1}
      locale={locale}
      t={t}
      selectedDate={date}
      setSelectedDate={setDate}
      selection={selection}
      setSelection={setSelection}
      localMode={localMode}
      setLocalMode={setLocalMode}
      {...opts}
    />
  );
};

const switchPeriod = (mode, inc, period, setPeriod) => {
  switch (mode) {
    case 'week':
      setPeriod({ start: startOfDay(addWeeks(period.start, inc)), end: endOfDay(addWeeks(period.end, inc)) });
      return;
    case 'month':
      setPeriod({
        start: startOfDay(startOfMonth(addMonths(period.start, inc))),
        end: endOfDay(endOfMonth(addMonths(period.end, inc))),
      });
      return;
    case 'quarter':
      setPeriod({
        start: startOfDay(startOfQuarter(addQuarters(period.start, inc))),
        end: endOfDay(endOfQuarter(addQuarters(period.end, inc))),
      });
      return;
    case 'year':
      setPeriod({
        start: startOfDay(startOfYear(addYears(period.start, inc))),
        end: endOfDay(endOfYear(addYears(period.end, inc))),
      });
      return;
    default:
      const days = inc * differenceInDays(period.start, period.end);
      setPeriod({ start: startOfDay(addDays(period.start, days)), end: endOfDay(addDays(period.end, days)) });
      return;
  }
};

const DatePeriodPicker = ({ view, popupPosition, defaultMode, ...opts }) => {
  const [t] = useTranslation();
  const { value, setValue } = opts;
  const [isOpen, setOpen] = useState(false);
  const [mode, setMode] = useState(defaultMode);

  useEffect(() => {
    setOpen(false);
  }, [value]);

  return (
    view === 'switch' && (
      <Downshift isOpen={isOpen} onOuterClick={() => setOpen(false)}>
        {({ isOpen }) => (
          <div className={s.switcherContainer}>
            <div className={s.switcherControls}>
              <DateShortcuts t={t} setSelection={setValue} mode={mode} setMode={setMode} />
              <Button
                className={cn(s.shortcut, s.prevPeriod)}
                icons="only"
                theme="text"
                onClick={() => switchPeriod(mode, -1, value, setValue)}
              >
                <RightArrow />
              </Button>
              <Button className={cn(s.shortcut, s.periodSelector)} theme="text" onClick={() => setOpen(true)}>
                {t('{{date, date}}', { date: value.start })} — {t('{{date, date}}', { date: value.end })}
              </Button>
              <Button
                className={cn(s.shortcut, s.nextPeriod)}
                icons="only"
                theme="text"
                onClick={() => switchPeriod(mode, 1, value, setValue)}
              >
                <RightArrow />
              </Button>
            </div>
            {isOpen && (
              <div className={cn(s.calendarPopup, s[popupPosition])}>
                <DatePopup mode={mode} setMode={setMode} {...opts} />
              </div>
            )}
          </div>
        )}
      </Downshift>
    )
  );
};

DatePeriodPicker.propTypes = {
  defaultMode: PropTypes.oneOf(['week', 'month', 'quarter', 'year', 'custom']),
  view: PropTypes.oneOf(['switch']),
  theme: PropTypes.oneOf(['toolbar-white']),
  popupPosition: PropTypes.oneOf(['bottom-left']),
  value: PropTypes.shape({
    start: PropTypes.instanceOf(Date).isRequired,
    end: PropTypes.instanceOf(Date).isRequired,
  }),
};

DatePeriodPicker.defaultProps = {
  view: 'switch',
  defaultMode: 'month',
  theme: 'toolbar-white',
};

export default DatePeriodPicker;
